import type { Permission } from "@tcg/api-client";
import { INBOX_TOPIC, type StaffArea, staffTopic } from "@tcg/realtime";

/**
 * The permission the realtime hub checks for each `staff:<area>` topic (services/realtime
 * `app/topics.py` STAFF_TOPICS). The admin subscribes only to the areas the signed-in person may
 * read: a refused topic would fall back to the 30 s poll for nothing.
 */
export const STAFF_TOPIC_PERMISSION: Readonly<Record<StaffArea, Permission>> = {
  orders: "orders.read",
  payments: "payments.read",
  inventory: "inventory.read",
  catalog: "products.read",
  consignment: "consignment.read",
  cms: "cms.edit",
  tax: "tax.read",
  pos: "pos.shift.manage",
  customers: "customers.read",
  inbox: "inbox.read",
  staff: "staff.read",
  audit: "audit.view",
  notifications: "notifications.read",
};

/** Permission needed to hear `topic` (`null` = no staff permission known for it). */
export function topicPermission(topic: string): Permission | null {
  if (topic === INBOX_TOPIC) {
    return "inbox.read";
  }
  if (topic.startsWith("inventory.changed:")) {
    return "inventory.read";
  }
  if (topic.startsWith("staff:")) {
    const area = topic.slice("staff:".length) as StaffArea;
    return STAFF_TOPIC_PERMISSION[area] ?? null;
  }
  return null;
}

/** Keep the topics this person may hear. */
export function allowedTopics(
  topics: readonly string[],
  can: (permission: Permission) => boolean,
): string[] {
  return topics.filter((topic) => {
    const permission = topicPermission(topic);
    return permission === null || can(permission);
  });
}

export { staffTopic };
